import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchApi } from './fetchApi';

interface Position {
  id: number;
  artikel_id: number;
  menge: number;
  sku: string;
  bezeichnung: string;
}

interface LieferscheinDaten {
  id: number;
  lieferschein_nr: string;
  quelle_typ: string;
  quelle_id: number | null;
  ziel_typ: string;
  ziel_id: number | null;
  bemerkung: string | null;
  status: string;
  erstellt_am: string;
  empfaenger: string;
  quelle_name: string;        // <-- NEU
  positionen: Position[];
}

interface Artikel {
  id: number;
  parent_id: number | null;
  sku: string;
  bezeichnung: string;
}

export default function LieferscheinBearbeiten() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [daten, setDaten] = useState<LieferscheinDaten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const [parents, setParents] = useState<Artikel[]>([]);
  const [childs, setChilds] = useState<Artikel[]>([]);

  // neue Position
  const [parentNeu, setParentNeu] = useState<number | ''>('');
  const [childNeu, setChildNeu] = useState<number | ''>('');
  const [mengeNeu, setMengeNeu] = useState<number | ''>('');
  const [verfuegbarNeu, setVerfuegbarNeu] = useState<number | null>(null);

  // Tracking für Speichern
  const [deleted, setDeleted] = useState<number[]>([]);
  const [modified, setModified] = useState<{ id: number; menge: number }[]>([]);
  const [added, setAdded] = useState<{ artikel_id: number; menge: number }[]>([]);

  useEffect(() => {
    async function lade() {
      try {
        const res = await fetchApi(`/api/lieferschein/view/${id}`);
        const json = await res.json();
        setDaten(json);

        const artRes = await fetchApi('/api/artikel');
        const artikel = await artRes.json();

        setParents(artikel.filter((a: Artikel) => a.parent_id === null));
        setChilds(artikel.filter((a: Artikel) => a.parent_id !== null));

      } catch (e: any) {
        setFehler(
          e?.message === '401'
            ? 'Session abgelaufen. Bitte neu einloggen.'
            : 'Fehler beim Laden.'
        );
      } finally {
        setLaden(false);
      }
    }

    lade();
  }, [id]);

  // Verfügbarkeit laden
  async function holeVerfuegbar(childId: number) {
    if (!daten) return;

    if (daten.quelle_typ !== 'LAGERORT') {
      setVerfuegbarNeu(null);
      return;
    }

    const res = await fetchApi(
  `/api/view/verfuegbar?artikel_id=${childId}&lagerort_id=${daten.quelle_id}`
);
    const json = await res.json();
    setVerfuegbarNeu(json.verfuegbar);
  }

  async function speichernGesamt() {
    if (!daten) return;
    if (daten.status !== 'offen') {
      alert('Nicht erlaubt – Lieferschein ist bereits versendet.');
      return;
    }

    const payload = {
      bemerkung: daten.bemerkung,
      positionen_update: modified,
      positionen_neu: added,
      positionen_loeschen: deleted
    };

    try {
      await fetchApi(`/api/lieferschein/${daten.id}/edit`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      alert('Änderungen gespeichert.');
      navigate(`/lieferschein/${daten.id}`);
    } catch (e: any) {
      alert(
        e?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : `Fehler beim Speichern: ${e?.message || 'Unbekannter Fehler'}`
      );
    }
  }

  if (laden) {
    return (
      <Container><Box mt={5}><CircularProgress /></Box></Container>
    );
  }

  if (!daten) {
    return <Alert severity="error">{fehler || 'Keine Daten.'}</Alert>;
  }

  return (
    <Container sx={{ mt: 4, mb: 8 }}>

      {/* Kopfbereich */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>




        <Typography variant="h5">
          Lieferschein bearbeiten – {daten.lieferschein_nr}
        </Typography>

<Box
  sx={{
    backgroundColor: '#f7f7f7',
    padding: 2,
    borderRadius: 2,
    mb: 3,
    border: '1px solid #e0e0e0'
  }}
>
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <Typography variant="subtitle2">Sender:</Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {daten.quelle_name || `${daten.quelle_typ}${daten.quelle_id ? ` #${daten.quelle_id}` : ''}`}
      </Typography>
    </Grid>

    <Grid item xs={12} sm={6}>
      <Typography variant="subtitle2">Empfänger:</Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {daten.empfaenger}
      </Typography>
    </Grid>

    <Grid item xs={12}>
      <Typography variant="subtitle2">Erstellt am:</Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {new Date(daten.erstellt_am).toLocaleString('de-AT')}
      </Typography>
    </Grid>
  </Grid>
</Box>


        <Box>
          <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate(`/lieferschein/${daten.id}`)}>
            Zurück
          </Button>

          {daten.status === 'offen' && (
            <Button variant="contained" onClick={speichernGesamt}>
              Änderungen speichern
            </Button>
          )}
        </Box>
      </Box>

      {daten.status !== 'offen' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Dieser Lieferschein wurde bereits versendet und ist nicht mehr bearbeitbar.
        </Alert>
      )}

      <Typography variant="h6">Bemerkung</Typography>
      <TextField
        fullWidth
        multiline
        rows={2}
        sx={{ mt: 1, mb: 3 }}
        disabled={daten.status !== 'offen'}
        value={daten.bemerkung || ''}
        onChange={e => setDaten({ ...daten, bemerkung: e.target.value })}
      />

      {/* Positionen */}
      <Typography variant="h6">Positionen</Typography>

      <Table sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>SKU</TableCell>
            <TableCell>Artikel</TableCell>
            <TableCell align="right">Menge</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {daten.positionen
            .filter(p => !deleted.includes(p.id))
            .map(p => (
              <TableRow key={p.id}>
                <TableCell>{p.sku}</TableCell>
                <TableCell>{p.bezeichnung}</TableCell>

                {/* Menge änderbar */}
                <TableCell align="right">
                  <TextField
                    type="number"
                    sx={{ width: 90 }}
                    disabled={daten.status !== 'offen'}
                    defaultValue={p.menge}
                    onBlur={(e) => {
                      const newVal = Number(e.target.value);
                      setModified(prev => [...prev.filter(m => m.id !== p.id), { id: p.id, menge: newVal }]);
                    }}
                  />
                </TableCell>

                <TableCell align="right">
                  {daten.status === 'offen' && (
                    <IconButton
                      color="error"
                      onClick={() => setDeleted(prev => [...prev, p.id])}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {/* Neue Position */}
      {daten.status === 'offen' && (
        <>
          <Typography variant="h6" sx={{ mt: 5 }}>Neue Position hinzufügen</Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Parent</InputLabel>
                <Select
                  label="Parent"
                  value={parentNeu}
                  onChange={(e) => {
                    setParentNeu(Number(e.target.value));
                    setChildNeu('');
                    setVerfuegbarNeu(null);
                  }}
                >
                  {parents.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.sku} – {p.bezeichnung}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Variante</InputLabel>
                <Select
                  label="Variante"
                  value={childNeu}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setChildNeu(val);
                    holeVerfuegbar(val);
                  }}
                  disabled={!parentNeu}
                >
                  {childs
                    .filter(c => c.parent_id === parentNeu)
                    .map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.sku} – {c.bezeichnung}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                label="Verfügbar"
                fullWidth
                value={
                  daten.quelle_typ === 'LAGERORT'
                    ? (verfuegbarNeu ?? '–')
                    : '∞'
                }
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Menge"
                type="number"
                value={mengeNeu}
                onChange={(e) => setMengeNeu(Number(e.target.value))}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={() => {
                  if (!childNeu || !mengeNeu) {
                    alert('Bitte Artikel und Menge angeben.');
                    return;
                  }

                  if (
                    daten.quelle_typ === 'LAGERORT' &&
                    verfuegbarNeu !== null &&
                    mengeNeu > verfuegbarNeu
                  ) {
                    alert('Menge überschreitet Verfügbarkeit.');
                    return;
                  }

// Neue Position in "added" registrieren
const newPos = {
  id: -Date.now(),                   // Fake-ID
  artikel_id: Number(childNeu),
  menge: Number(mengeNeu),
  sku: childs.find(c => c.id === childNeu)?.sku || '',
  bezeichnung: childs.find(c => c.id === childNeu)?.bezeichnung || ''
};

// 1. Merken für Speichern
setAdded(prev => [
  ...prev,
  { artikel_id: newPos.artikel_id, menge: newPos.menge }
]);

// 2. Sofort in UI anzeigen
setDaten(prev => ({
  ...prev!,
  positionen: [...prev!.positionen, newPos]
}));

// Felder zurücksetzen
setParentNeu('');
setChildNeu('');
setMengeNeu('');
setVerfuegbarNeu(null);

                }}
              >
                Position hinzufügen
              </Button>
            </Grid>
          </Grid>
        </>
      )}

    </Container>
  );
}
